import { CustomMetadataField } from "vl-api/vlSchemas";
import FolderIcon from "assets/icons/FolderIcon";
import { FileIcon } from "assets/icons/FileIcon";
import TagIcon from "assets/icons/tagIcon";
import UserTagIcon from "assets/icons/UserTagIcon";
import CaptionIcon from "assets/icons/CaptionIcon";
import MislabelsIcon from "assets/icons/MislabelsIcon";
import LabelOutliersIcon from "assets/icons/LabelOutliersIcon";
import DuplicateIcon from "assets/icons/DuplicateIcon";
import OutliersIcon from "assets/icons/OutliersIcon";
import QualityIssuesIcon from "assets/icons/QualityIssuesIcon";
import UniquenessIcon from "assets/icons/UniquenessIcon";
import ObjectSizeIcon from "assets/icons/ObjectSizeIcon";
import { DateTimeIcon } from "assets/icons/CustomMetadataIcons";
import { EntityType, FilterMenuItem, Filters } from "../types";
import { createCustomMetadataFieldFilterMenuItem } from "../helpers/utility/customMetadataHelpers";
import { useFeatureFlagService } from "./useFeatureFlagService";
import {
  CAPTIONS_FILTER,
  CLUSTER_BY_DUPLICATES,
  OBJECT_SIZE_FILTER_ENABLED,
  UNIQUENESS_SCORE,
} from "../helpers/constants/filters";
import { useIssuesData } from "views/pages/DataPage/CardsView/DataPageFilters/IssuesQueryModal/useIssuesData";

interface UseFilterConfigParams {
  captionsEnabled: boolean;
  entityType: EntityType | null;
  customMetadataData?: CustomMetadataField[] | null;
}

const RANGE_ISSUES = ["OUTLIERS", "MISLABELS", "DUPLICATES", "LABEL OUTLIER"];

export const useFilterConfig = ({
  captionsEnabled,
  entityType,
  customMetadataData,
}: UseFilterConfigParams): FilterMenuItem[] => {
  const isUniquenessEnabled = useFeatureFlagService(UNIQUENESS_SCORE).enabled;
  const isDuplicatesThresholdEnabled = useFeatureFlagService(
    CLUSTER_BY_DUPLICATES,
  ).enabled;
  const isNewObjectSizeFilterEnabled = useFeatureFlagService(
    OBJECT_SIZE_FILTER_ENABLED,
  ).enabled;
  const { issues } = useIssuesData();
  const customMetadata = customMetadataData || [];

  const disabledUniqueness = !useFeatureFlagService(UNIQUENESS_SCORE).enabled;
  const isCaptionsFilterVisible = useFeatureFlagService(CAPTIONS_FILTER).show;

  const availableIssues = new Set(
    issues.map((i) => i.text.toUpperCase().replace(/\s+/g, "_")),
  );

  const hasNonRangeIssues = issues.some(
    (i) => !RANGE_ISSUES.includes(i.text.toUpperCase()),
  );

  // --- Static Filters ---
  const filterConfig: FilterMenuItem[] = [
    {
      icon: <FolderIcon />,
      type: Filters.FOLDER,
      entityTypes: ["IMAGES", "OBJECTS"],
      hidden: false,
    },
    {
      icon: <FileIcon />,
      type: Filters.FILE,
      entityTypes: ["IMAGES", "OBJECTS"],
      hidden: false,
    },
    {
      icon: <TagIcon />,
      type: Filters.LABELS,
      entityTypes: ["IMAGES", "OBJECTS"],
      disabled: false,
      hidden: false,
    },
    {
      icon: <UserTagIcon />,
      type: Filters.USER_TAGS,
      entityTypes: ["IMAGES", "OBJECTS"],
      hidden: false,
    },
    {
      icon: <CaptionIcon type="IMAGE" />,
      type: Filters.CAPTION,
      entityTypes: ["IMAGES", "OBJECTS"],
      disabled: !captionsEnabled,
      hidden: !isCaptionsFilterVisible,
    },
    {
      icon: <MislabelsIcon />,
      type: Filters.MISLABELS,
      entityTypes: ["IMAGES", "OBJECTS"],
      hidden: !availableIssues.has(Filters.MISLABELS),
    },
    {
      icon: <LabelOutliersIcon />,
      type: Filters.LABEL_OUTLIER,
      entityTypes: ["IMAGES", "OBJECTS"],
      hidden: !availableIssues.has(Filters.LABEL_OUTLIER),
    },
    {
      icon: <DuplicateIcon />,
      type: Filters.DUPLICATES,
      entityTypes: ["IMAGES", "OBJECTS"],
      disabled: !isDuplicatesThresholdEnabled,
      hidden: !availableIssues.has(Filters.DUPLICATES),
    },
    {
      icon: <OutliersIcon />,
      type: Filters.OUTLIERS,
      entityTypes: ["IMAGES", "OBJECTS"],
      hidden: !availableIssues.has(Filters.OUTLIERS),
    },
    {
      icon: <QualityIssuesIcon />,
      type: Filters.ISSUES,
      entityTypes: ["IMAGES", "OBJECTS"],
      hidden: !hasNonRangeIssues,
    },
    {
      icon: <UniquenessIcon />,
      type: Filters.UNIQUENESS,
      entityTypes: ["IMAGES", "OBJECTS"],
      hidden: !isUniquenessEnabled,
      disabled: disabledUniqueness,
    },
    {
      icon: <ObjectSizeIcon />,
      type: Filters.SIZE,
      entityTypes: ["OBJECTS"],
      hidden: !isNewObjectSizeFilterEnabled,
    },
    {
      icon: <DateTimeIcon />,
      type: Filters.CREATED_AT,
      entityTypes: ["IMAGES", "OBJECTS"],
      hidden: false,
    },
    {
      icon: <TagIcon />,
      type: Filters.MEDIA_STATUS,
      entityTypes: ["IMAGES", "OBJECTS"],
      hidden: false,
    },
  ];

  // --- Combine and filter ---
  return [
    ...filterConfig,
    ...(customMetadata
      ?.filter(
        (field: CustomMetadataField) => field.field_type !== "geolocation",
      )
      ?.map(createCustomMetadataFieldFilterMenuItem) || []),
  ].filter(
    (item) =>
      !item.hidden &&
      (!item.entityTypes ||
        entityType === null ||
        item.entityTypes.includes(entityType)),
  );
};
